/**
 * Question Selector — v11.2 FamilyId-First Selection Algorithm
 *
 * The core question selection function that picks a single question
 * using strict pedagogical rules and group/tier gating.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import {
  type ModuleId,
  type Question,
  getQuestionWithExplanation,
  getFamiliesForModule,
  type FamilyDefinition,
  QUESTIONS_BY_FAMILY,
  getAllowedTiersForGroup,
  getTierRangeForGroup,
} from '../../content'
import { getLDG } from '../../governance'
import { GroupId } from '../../config/systemConfig'
import { FamilyMasteryState, getFamilyMasteryKey } from '../familyMasteryEngine'
import { computeDecayedMastery } from '../masteryDecay'
import { warn as logWarn } from '../../utils/logger'
import type { SeededRng } from '../../utils/deterministicRng'
import { V11_CONFIG, debugLog } from './selectionConfig'
import {
  computeFamilyPriority,
  computeMasteryConfidence,
  isMasteryStable,
  determineTargetTier,
} from './familyScoring'
import { buildTierWindow, findQuestionsForFamily } from './tierWindow'
import { arePrerequisitesMet } from './prerequisiteCheck'

/**
 * Select a single question using the v11.2 familyId-first algorithm
 * with strict pedagogical rules and group/tier gating.
 *
 * v11.2 Enhancements:
 * - Per-family question cap (max 2 per family)
 * - Recency-based question exclusion
 * - Mastery confidence checks
 * - Controlled reinforcement mode
 * - Soft preference for interactive types
 * v11.8: B/M/E period tier for priority boost (QF-26 Optie B)
 *
 * GROUP-SCOPED MASTERY: This function now uses group-scoped mastery keys internally.
 */
export function selectAdaptiveQuestionV11(
  moduleId: ModuleId,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  allowedTiers: Set<number>,
  sessionFamilyIds: Set<string>,
  sessionQuestionIds: Set<string>,
  recentFamilyIds: Set<string>,
  recentQuestionIds: Set<string>,
  sessionTypeHistory: string[],
  masteredCount: number,
  familyQuestionCounts: Map<string, number>,
  sessionSize: number,
  rng: SeededRng,
  effectiveMaxPerFamily: number = V11_CONFIG.MAX_QUESTIONS_PER_FAMILY,
  periodTier?: number,
): {
  question: Question | null
  reason: string
  fallbackLevel: number
} {
  // 1. Get all families for this module from taxonomy
  const allModuleFamilies = getFamiliesForModule(moduleId)
  if (allModuleFamilies.length === 0) {
    const reason = `No families found in taxonomy for module ${moduleId}`
    debugLog(`⚠️ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 0,
    }
  }

  // 2. CRITICAL: Filter families to only those whose tier is within allowed tiers
  const eligibleFamilies = allModuleFamilies.filter((f) =>
    allowedTiers.has(f.tier),
  )

  // 2b. NEW: Filter by LDG prerequisites (CONFLICT-01 resolution)
  const prerequisiteReadyFamilies = eligibleFamilies.filter((f) =>
    arePrerequisitesMet(f.familyId, familyMastery, selectedGroup),
  )

  // v11.4: When no families pass prerequisite check, fall back to ROOT families only
  // (families with no prerequisites in the LDG). This prevents serving advanced topics
  // when a student hasn't built foundations. Previously fell back to ALL eligible families
  // which completely bypassed the learning graph.
  let selectionPool: FamilyDefinition[]
  if (prerequisiteReadyFamilies.length > 0) {
    selectionPool = prerequisiteReadyFamilies
  } else {
    // Fallback: only root families (no prerequisites) — never bypass LDG entirely
    const graph = getLDG()
    const rootOnlyFamilies = eligibleFamilies.filter((f) => {
      const node = graph.nodes.get(f.familyId)
      return !node || node.prerequisites.length === 0
    })

    if (rootOnlyFamilies.length > 0) {
      selectionPool = rootOnlyFamilies
      debugLog(
        `🔒 LDG Gating: No prerequisite-ready families. Falling back to ${rootOnlyFamilies.length} root families (no prerequisites).`,
      )
    } else {
      // Emergency: if even no root families exist for this group/module, use all eligible
      // This should be extremely rare (only if module has no root-tier content for this group)
      selectionPool = eligibleFamilies
      debugLog(
        `⚠️ LDG Gating: No root families available either. Emergency fallback to all ${eligibleFamilies.length} eligible families.`,
      )
    }
  }

  if (prerequisiteReadyFamilies.length < eligibleFamilies.length) {
    debugLog(
      `🔒 LDG Gating: ${eligibleFamilies.length - prerequisiteReadyFamilies.length} families locked by prerequisites (${prerequisiteReadyFamilies.length}/${eligibleFamilies.length} ready, threshold: ${V11_CONFIG.LDG_PREREQUISITE_MASTERY})`,
    )
  }

  if (selectionPool.length === 0) {
    const reason = `No families for module ${moduleId} within allowed tiers [${Array.from(allowedTiers).join(',')}] for group ${selectedGroup}`
    debugLog(`⚠️ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 0,
    }
  }
  debugLog(
    `📋 v11.2: Module ${moduleId}, Group ${selectedGroup}, Allowed tiers: [${Array.from(
      allowedTiers,
    )
      .sort((a, b) => a - b)
      .join(
        ',',
      )}], Eligible families: ${selectionPool.length}/${allModuleFamilies.length} (${prerequisiteReadyFamilies.length} prereq-ready)`,
  )

  // v11.2: Calculate mastered cap — hard cap (MAX_MASTERED_PER_SESSION) combined with soft cap (70%)
  const maxMasteredAllowed = Math.min(
    V11_CONFIG.MAX_MASTERED_PER_SESSION,
    Math.ceil(sessionSize * V11_CONFIG.MAX_MASTERED_PERCENTAGE),
  )

  // 3. Score and sort families by priority
  // CRITICAL: Use group-scoped mastery keys
  const scoredFamilies = selectionPool
    .filter((f) => {
      // v11.2: Exclude families that hit per-family cap (use effective cap)
      const usageCount = familyQuestionCounts.get(f.familyId) || 0
      if (usageCount >= effectiveMaxPerFamily) {
        debugLog(
          `   ⚠️ Family ${f.familyId} hit per-family cap (${usageCount}/${effectiveMaxPerFamily})`,
        )
        return false
      }
      return true
    })
    .filter((f) => !sessionFamilyIds.has(f.familyId)) // Exclude already-used families (for diversity)
    .map((family) => {
      // CRITICAL: Look up mastery using group-scoped key
      const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
      const masteryState = familyMastery[masteryKey]
      // Apply decay to mastery for accurate priority scoring
      const decayedScore = computeDecayedMastery(masteryState)
      const effectiveState = masteryState
        ? { ...masteryState, masteryScore: decayedScore }
        : masteryState
      return {
        family,
        mastery: effectiveState,
        priority: computeFamilyPriority(
          family,
          effectiveState,
          recentFamilyIds,
          periodTier,
        ),
        confidence: computeMasteryConfidence(effectiveState),
      }
    })
    .filter((sf) => {
      // v11.2: Enforce mastered cap with percentage-based limit
      if (masteredCount >= maxMasteredAllowed) {
        const mastery = sf.mastery?.masteryScore ?? 0
        const isStable = isMasteryStable(sf.mastery)
        if (mastery >= V11_CONFIG.MASTERED_THRESHOLD && isStable) {
          return false // Exclude stable mastered families when at cap
        }
      }
      return true
    })
    .sort((a, b) => b.priority - a.priority)

  // NEW: Debug logging for filtering steps
  debugLog(`🔍 v11.2 Filter Chain:
    - Total eligible families: ${eligibleFamilies.length}
    - After per-family cap: ${eligibleFamilies.filter((f) => (familyQuestionCounts.get(f.familyId) || 0) < effectiveMaxPerFamily).length}
    - After session exclusion: ${eligibleFamilies.filter((f) => !sessionFamilyIds.has(f.familyId) && (familyQuestionCounts.get(f.familyId) || 0) < effectiveMaxPerFamily).length}
    - After mastered cap (${masteredCount}/${maxMasteredAllowed}): ${scoredFamilies.length}
    - Mastered families in pool: ${scoredFamilies.filter((sf) => (sf.mastery?.masteryScore ?? 0) >= V11_CONFIG.MASTERED_THRESHOLD).length}`)
  if (scoredFamilies.length === 0) {
    // v11.2: FALLBACK LEVEL 1 - Allow reusing families (but respect per-family cap)
    debugLog(
      `⚠️ v11.2: No new families available. FALLBACK LEVEL 1: Reusing families (respecting per-family cap)...`,
    )
    const reusableFamilies = eligibleFamilies
      .filter((f) => {
        const usageCount = familyQuestionCounts.get(f.familyId) || 0
        return usageCount < effectiveMaxPerFamily
      })
      .map((family) => {
        // CRITICAL: Look up mastery using group-scoped key
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
        const masteryState = familyMastery[masteryKey]
        return {
          family,
          mastery: masteryState,
          priority: computeFamilyPriority(
            family,
            masteryState,
            recentFamilyIds,
            periodTier,
          ),
          confidence: computeMasteryConfidence(masteryState),
        }
      })
      .filter((sf) => {
        // Still respect mastered cap
        if (masteredCount >= maxMasteredAllowed) {
          const mastery = sf.mastery?.masteryScore ?? 0
          const isStable = isMasteryStable(sf.mastery)
          if (mastery >= V11_CONFIG.MASTERED_THRESHOLD && isStable) {
            return false
          }
        }
        return true
      })
      .sort((a, b) => b.priority - a.priority)
    if (reusableFamilies.length > 0) {
      debugLog(
        `✅ v11.2: Found ${reusableFamilies.length} reusable families (FALLBACK LEVEL 1)`,
      )
      const poolSize = Math.min(
        V11_CONFIG.TOP_CANDIDATE_POOL,
        reusableFamilies.length,
      )
      const topCandidates = reusableFamilies.slice(0, poolSize)
      const selected = rng.pick(topCandidates)!
      const { family, mastery: masteryState } = selected
      debugLog(
        `🎯 v11.2: Selected family ${family.familyId} (FALLBACK LEVEL 1, tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0}, confidence: ${selected.confidence.toFixed(2)})`,
      )
      const targetTier = determineTargetTier(family, masteryState, allowedTiers)
      const fb1Mastery = masteryState?.masteryScore ?? 0
      const tierWindow = buildTierWindow(
        targetTier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
        fb1Mastery,
      )
      let candidates = findQuestionsForFamily(
        family.familyId,
        tierWindow,
        sessionQuestionIds,
      )

      // v11.2: Apply soft preference for interactive types if we've had too many MC questions
      if (
        candidates.length > 1 &&
        sessionTypeHistory.length >= V11_CONFIG.MAX_CONSECUTIVE_MC
      ) {
        const recentTypes = sessionTypeHistory.slice(
          -V11_CONFIG.MAX_CONSECUTIVE_MC,
        )
        if (recentTypes.every((t) => t === 'multiple_choice')) {
          const interactiveCandidates = candidates.filter(
            (q) => q.questionType && q.questionType !== 'multiple_choice',
          )
          if (interactiveCandidates.length > 0) {
            candidates = interactiveCandidates
            debugLog(
              `   🎲 Soft preference applied: filtered to ${interactiveCandidates.length} interactive questions after ${V11_CONFIG.MAX_CONSECUTIVE_MC} consecutive MC`,
            )
          }
        }
      }

      // v11.2: Exclude recent questions unless no alternatives
      const nonRecentCandidates = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentCandidates.length > 0) {
        candidates = nonRecentCandidates
        debugLog(
          `   🕐 Excluded ${candidates.length - nonRecentCandidates.length} recent questions`,
        )
      }
      if (candidates.length === 0) {
        const expandedWindow = buildTierWindow(
          targetTier,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          allowedTiers,
          fb1Mastery,
        )
        candidates = findQuestionsForFamily(
          family.familyId,
          expandedWindow,
          sessionQuestionIds,
        )
        const nonRecentExpanded = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
      }
      if (candidates.length === 0) {
        candidates = findQuestionsForFamily(
          family.familyId,
          allowedTiers,
          sessionQuestionIds,
        )
        const nonRecentAll = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentAll.length > 0) candidates = nonRecentAll
      }
      if (candidates.length > 0) {
        const shuffled = rng.shuffle(candidates)
        const picked = shuffled[0]
        debugLog(
          `✅ v11.2: Picked question ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier}) - FALLBACK LEVEL 1`,
        )
        return {
          question: getQuestionWithExplanation(picked.id),
          reason: 'family reuse (respecting per-family cap)',
          fallbackLevel: 1,
        }
      }
    }

    // v11.2: FALLBACK LEVEL 2 - Controlled reinforcement (mastered families)
    debugLog(
      `⚠️ v11.2: FALLBACK LEVEL 2: Controlled reinforcement (mastered families)...`,
    )
    const masteredFamilies = eligibleFamilies
      .filter((f) => {
        const usageCount = familyQuestionCounts.get(f.familyId) || 0
        return usageCount < effectiveMaxPerFamily
      })
      .map((family) => {
        // CRITICAL: Look up mastery using group-scoped key
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
        const masteryState = familyMastery[masteryKey]
        return {
          family,
          mastery: masteryState,
          priority: computeFamilyPriority(
            family,
            masteryState,
            recentFamilyIds,
            periodTier,
          ),
          confidence: computeMasteryConfidence(masteryState),
        }
      })
      .filter((sf) => {
        const mastery = sf.mastery?.masteryScore ?? 0
        const isStable = isMasteryStable(sf.mastery)
        return mastery >= V11_CONFIG.MASTERED_THRESHOLD && isStable
      })
      .sort((a, b) => b.priority - a.priority)
    if (masteredFamilies.length > 0) {
      debugLog(
        `✅ v11.2: Found ${masteredFamilies.length} mastered families for reinforcement (FALLBACK LEVEL 2)`,
      )
      const poolSize = Math.min(
        V11_CONFIG.TOP_CANDIDATE_POOL,
        masteredFamilies.length,
      )
      const topCandidates = masteredFamilies.slice(0, poolSize)
      const selected = rng.pick(topCandidates)!
      const { family, mastery: masteryState } = selected
      debugLog(
        `🎯 v11.2: Selected MASTERED family for reinforcement: ${family.familyId} (tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0}, confidence: ${selected.confidence.toFixed(2)})`,
      )
      const targetTier = determineTargetTier(family, masteryState, allowedTiers)
      const fb2Mastery = masteryState?.masteryScore ?? 0
      const tierWindow = buildTierWindow(
        targetTier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
        fb2Mastery,
      )
      let candidates = findQuestionsForFamily(
        family.familyId,
        tierWindow,
        sessionQuestionIds,
      )
      const nonRecentCandidates = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentCandidates.length > 0) candidates = nonRecentCandidates
      if (candidates.length === 0) {
        const expandedWindow = buildTierWindow(
          targetTier,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          allowedTiers,
          fb2Mastery,
        )
        candidates = findQuestionsForFamily(
          family.familyId,
          expandedWindow,
          sessionQuestionIds,
        )
        const nonRecentExpanded = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
      }
      if (candidates.length === 0) {
        candidates = findQuestionsForFamily(
          family.familyId,
          allowedTiers,
          sessionQuestionIds,
        )
        const nonRecentAll = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentAll.length > 0) candidates = nonRecentAll
      }
      if (candidates.length > 0) {
        const shuffled = rng.shuffle(candidates)
        const picked = shuffled[0]
        debugLog(
          `✅ v11.2: Picked reinforcement question ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier})`,
        )
        return {
          question: getQuestionWithExplanation(picked.id),
          reason: 'reinforcement (controlled, stable mastery)',
          fallbackLevel: 2,
        }
      }
    }

    // v11.2: FALLBACK LEVEL 3 - Emergency (any family, ignore all caps)
    // G-5: Emergency fallback telemetry — log structured data for monitoring
    logWarn(
      `🚨 LDG Emergency Fallback triggered: module=${moduleId}, group=${selectedGroup}, ` +
        `sessionQuestions=${sessionQuestionIds.size}, allowedTiers=[${Array.from(allowedTiers).join(',')}]. ` +
        `This means the normal LDG selection path found no suitable questions.`,
    )
    debugLog(`⚠️ v11.2: FALLBACK LEVEL 3: Emergency fallback (ignore caps)...`)
    const anyFamilies = eligibleFamilies
      .map((family) => {
        // CRITICAL: Look up mastery using group-scoped key
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
        const masteryState = familyMastery[masteryKey]
        return {
          family,
          mastery: masteryState,
          priority: computeFamilyPriority(
            family,
            masteryState,
            recentFamilyIds,
            periodTier,
          ),
          confidence: computeMasteryConfidence(masteryState),
        }
      })
      .sort((a, b) => b.priority - a.priority)
    if (anyFamilies.length > 0) {
      debugLog(
        `✅ v11.2: Found ${anyFamilies.length} families (FALLBACK LEVEL 3 - emergency)`,
      )
      const poolSize = Math.min(
        V11_CONFIG.TOP_CANDIDATE_POOL,
        anyFamilies.length,
      )
      const topCandidates = anyFamilies.slice(0, poolSize)
      const selected = rng.pick(topCandidates)!
      const { family, mastery: masteryState } = selected
      debugLog(
        `🎯 v11.2: Selected ANY family (FALLBACK LEVEL 3): ${family.familyId} (tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0})`,
      )
      const targetTier = determineTargetTier(family, masteryState, allowedTiers)
      const fb3Mastery = masteryState?.masteryScore ?? 0
      const tierWindow = buildTierWindow(
        targetTier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
        fb3Mastery,
      )
      let candidates = findQuestionsForFamily(
        family.familyId,
        tierWindow,
        sessionQuestionIds,
      )
      const nonRecentCandidates = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentCandidates.length > 0) candidates = nonRecentCandidates
      if (candidates.length === 0) {
        const expandedWindow = buildTierWindow(
          targetTier,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          allowedTiers,
          fb3Mastery,
        )
        candidates = findQuestionsForFamily(
          family.familyId,
          expandedWindow,
          sessionQuestionIds,
        )
        const nonRecentExpanded = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
      }
      if (candidates.length === 0) {
        candidates = findQuestionsForFamily(
          family.familyId,
          allowedTiers,
          sessionQuestionIds,
        )
        // Allow recent questions in emergency
      }
      if (candidates.length > 0) {
        const shuffled = rng.shuffle(candidates)
        const picked = shuffled[0]
        debugLog(
          `✅ v11.2: Picked emergency question ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier})`,
        )
        return {
          question: getQuestionWithExplanation(picked.id),
          reason: 'emergency fallback (all caps ignored)',
          fallbackLevel: 3,
        }
      }
    }
    const reason = `No questions available for ${moduleId} in group ${selectedGroup} (allowed tiers: [${Array.from(allowedTiers).join(',')}]) after all fallbacks`
    debugLog(`❌ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 4,
    }
  }

  // 4. Pick from top candidates (with seeded randomness for variety)
  const poolSize = Math.min(
    V11_CONFIG.TOP_CANDIDATE_POOL,
    scoredFamilies.length,
  )
  const topCandidates = scoredFamilies.slice(0, poolSize)
  const selected = rng.pick(topCandidates)!
  const { family, mastery: masteryState, confidence } = selected
  debugLog(
    `🎯 v11.2: Selected family ${family.familyId} (tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0}, priority: ${selected.priority}, confidence: ${confidence.toFixed(2)})`,
  )

  // 5. Determine target tier (clamped to allowed tiers)
  const targetTier = determineTargetTier(family, masteryState, allowedTiers)

  // v11.3: Extract current mastery for plus-tier gating in tier windows
  const currentMastery = masteryState?.masteryScore ?? 0

  // 6. Build tier window and find questions (intersected with allowed tiers)
  const tierWindow = buildTierWindow(
    targetTier,
    V11_CONFIG.TIER_WINDOW_BELOW,
    V11_CONFIG.TIER_WINDOW_ABOVE,
    allowedTiers,
    currentMastery,
  )
  let candidates = findQuestionsForFamily(
    family.familyId,
    tierWindow,
    sessionQuestionIds,
  )

  // v11.2: Apply soft preference for interactive types if we've had too many MC questions
  if (
    candidates.length > 1 &&
    sessionTypeHistory.length >= V11_CONFIG.MAX_CONSECUTIVE_MC
  ) {
    const recentTypes = sessionTypeHistory.slice(-V11_CONFIG.MAX_CONSECUTIVE_MC)
    if (recentTypes.every((t) => t === 'multiple_choice')) {
      const interactiveCandidates = candidates.filter(
        (q) => q.questionType && q.questionType !== 'multiple_choice',
      )
      if (interactiveCandidates.length > 0) {
        candidates = interactiveCandidates
        debugLog(
          `   🎲 Soft preference applied: filtered to ${interactiveCandidates.length} interactive questions after ${V11_CONFIG.MAX_CONSECUTIVE_MC} consecutive MC`,
        )
      }
    }
  }

  // v11.2: Exclude recent questions unless no alternatives
  const nonRecentCandidates = candidates.filter(
    (q) => !recentQuestionIds.has(q.id),
  )
  if (nonRecentCandidates.length > 0) {
    candidates = nonRecentCandidates
    debugLog(
      `   🕐 Excluded ${candidates.length - nonRecentCandidates.length} recent questions`,
    )
  }
  let fallbackReason = ''

  // 7. Fallback: expand tier window (still within allowed tiers)
  if (candidates.length === 0) {
    fallbackReason = `expanded window (±${V11_CONFIG.TIER_WINDOW_FALLBACK})`
    debugLog(
      `⚠️ v11.2: No questions for ${family.familyId} at tier ${targetTier} (±1). Expanding window within allowed tiers...`,
    )
    const expandedWindow = buildTierWindow(
      targetTier,
      V11_CONFIG.TIER_WINDOW_FALLBACK,
      V11_CONFIG.TIER_WINDOW_FALLBACK,
      allowedTiers,
      currentMastery,
    )
    candidates = findQuestionsForFamily(
      family.familyId,
      expandedWindow,
      sessionQuestionIds,
    )
    const nonRecentExpanded = candidates.filter(
      (q) => !recentQuestionIds.has(q.id),
    )
    if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
  }

  // 8. Fallback: try ANY allowed tier for this family (but NEVER outside group)
  if (candidates.length === 0) {
    fallbackReason = 'any allowed tier'
    debugLog(
      `⚠️ v11.2: No questions for ${family.familyId} in expanded window. Trying any ALLOWED tier...`,
    )
    candidates = findQuestionsForFamily(
      family.familyId,
      allowedTiers,
      sessionQuestionIds,
    )
    const nonRecentAll = candidates.filter((q) => !recentQuestionIds.has(q.id))
    if (nonRecentAll.length > 0) candidates = nonRecentAll
  }

  // 9. Fallback: try adjacent families (same skill, within allowed tiers)
  if (candidates.length === 0) {
    fallbackReason = 'adjacent family'
    debugLog(
      `⚠️ v11.2: No questions at all for ${family.familyId} within allowed tiers. Trying adjacent families...`,
    )
    const adjacentFamilies = eligibleFamilies.filter(
      (f) =>
        f.familyId !== family.familyId &&
        f.skill === family.skill &&
        (familyQuestionCounts.get(f.familyId) || 0) < effectiveMaxPerFamily,
    )
    for (const adjFamily of adjacentFamilies) {
      // v11.3: Look up mastery for adjacent family for plus-tier gating
      const adjMasteryKey = getFamilyMasteryKey(
        adjFamily.familyId,
        selectedGroup,
      )
      const adjMasteryState = familyMastery[adjMasteryKey]
      const adjMastery = adjMasteryState
        ? computeDecayedMastery(adjMasteryState)
        : 0
      const adjWindow = buildTierWindow(
        adjFamily.tier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
        adjMastery,
      )
      candidates = findQuestionsForFamily(
        adjFamily.familyId,
        adjWindow,
        sessionQuestionIds,
      )
      const nonRecentAdj = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentAdj.length > 0) {
        candidates = nonRecentAdj
        debugLog(
          `✅ v11.2: Found question via adjacent family ${adjFamily.familyId} (tier ${adjFamily.tier})`,
        )
        break
      }
      candidates = findQuestionsForFamily(
        adjFamily.familyId,
        allowedTiers,
        sessionQuestionIds,
      )
      const nonRecentAdjAll = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentAdjAll.length > 0) {
        candidates = nonRecentAdjAll
        debugLog(
          `✅ v11.2: Found question via adjacent family ${adjFamily.familyId} (any allowed tier)`,
        )
        break
      }
    }
  }

  // 10. Final fallback: try ANY eligible family at ANY allowed tier
  if (candidates.length === 0) {
    fallbackReason = 'any eligible family, any allowed tier'
    debugLog(
      `⚠️ v11.2: Adjacent families exhausted. Trying any eligible family at any allowed tier...`,
    )
    for (const f of eligibleFamilies) {
      const usageCount = familyQuestionCounts.get(f.familyId) || 0
      if (usageCount >= effectiveMaxPerFamily) continue
      candidates = findQuestionsForFamily(
        f.familyId,
        allowedTiers,
        sessionQuestionIds,
      )
      const nonRecentFinal = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentFinal.length > 0) {
        candidates = nonRecentFinal
        debugLog(`✅ v11.2: Found question via fallback family ${f.familyId}`)
        break
      }
    }
  }
  if (candidates.length === 0) {
    const reason = `No questions available for ${moduleId} in group ${selectedGroup} (allowed tiers: [${Array.from(allowedTiers).join(',')}]) after all fallbacks`
    debugLog(`❌ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 4,
    }
  }

  // 11. Pick a question (seeded random)
  const shuffled = rng.shuffle(candidates)
  const picked = shuffled[0]
  const reason = fallbackReason
    ? `selected via fallback: ${fallbackReason}`
    : `direct match (family: ${family.familyId}, targetTier: ${targetTier})`
  debugLog(
    `✅ v11.2: Picked ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier}) — ${reason}`,
  )
  return {
    question: getQuestionWithExplanation(picked.id),
    reason,
    fallbackLevel: 0,
  }
}
